<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <div class="dashboard-editor">
    <!-- Main Grid Editor -->
    <div ref="scrollContainer" class="editor-main">
      <div class="toolbar-row">
        <div class="toolbar-group">
          <label>{{ $t("dashboardAdmin.gridSettings.columns") }}:</label>
          <PrimeInputNumber
            v-model="gridSettings.columns"
            :min="1"
            :max="12"
            @update:modelValue="handleUpdateGridSettings"
            class="grid-size-input"
          />
        </div>
        <div class="toolbar-group">
          <label>{{ $t("dashboardAdmin.gridSettings.rows") }}:</label>
          <PrimeInputNumber
            v-model="gridSettings.rows"
            :min="1"
            :max="20"
            @update:modelValue="handleUpdateGridSettings"
            class="grid-size-input"
          />
        </div>
        <div class="toolbar-actions">
          <PrimeButton
            :label="$t('dashboardAdmin.addBlock')"
            icon="ri-add-line"
            @click="addNewBlock"
            size="small"
          />
          <PrimeButton
            :label="$t('dashboardAdmin.basket')"
            icon="ri-inbox-line"
            @click="showBasket = !showBasket"
            size="small"
            severity="secondary"
            :outlined="!showBasket"
            :badge="
              basketBlocks.length > 0 ? basketBlocks.length.toString() : null
            "
          />
          <PrimeButton
            :label="$t('dashboardAdmin.minimap')"
            icon="ri-map-2-line"
            @click="showMinimap = !showMinimap"
            size="small"
            severity="secondary"
            :outlined="!showMinimap"
          />
          <PrimeButton
            :label="$t('dashboardAdmin.preview')"
            icon="ri-eye-line"
            @click="showPreview = true"
            size="small"
            severity="info"
          />
        </div>
        <div class="toolbar-spacer"></div>
        <div class="block-count">
          {{ blocks.length }}
          {{
            blocks.length === 1
              ? $t("dashboardAdmin.block")
              : $t("dashboardAdmin.blocks")
          }}
        </div>
      </div>

      <!-- Grid Editor Component -->
      <GridEditor
        :grid-settings="gridSettings"
        :blocks="blocks"
        :drag-preview="dragPreview"
        :selected-block-id="selectedBlockId"
        :overlapping-block-ids="overlappingBlockIds"
        @dragstart="onDragStart"
        @dragend="onDragEnd"
        @dragover="onDragOver"
        @dragleave="onDragLeave"
        @drop="onDrop"
        @select-block="selectBlock"
        @edit-block="editBlock"
        @duplicate-block="duplicateBlock"
        @delete-block="deleteBlockById"
        @move-to-basket="moveToBasket"
        @resize-start="startResize"
      />

      <!-- Basket Panel Component -->
      <BasketPanel
        v-if="showBasket"
        :basket-blocks="basketBlocks"
        :selected-block-id="selectedBlockId"
        @basket-dragstart="onBasketDragStart"
        @basket-dragend="onDragEnd"
        @select-block="selectBlock"
        @edit-block="editBlock"
        @delete-block="deleteBasketBlock"
        @close="showBasket = false"
      />

      <!-- Minimap Panel Component -->
      <MinimapPanel
        v-if="showMinimap"
        :grid-settings="gridSettings"
        :blocks="blocks"
        :selected-block-id="selectedBlockId"
        @minimap-click="onMinimapClick"
        @close="showMinimap = false"
      />
    </div>

    <!-- Preview Dialog -->
    <PrimeDialog
      v-model:visible="showPreview"
      :modal="true"
      :dismissableMask="true"
      :closeOnEscape="true"
      :style="{ width: '95vw', height: '95vh' }"
      :contentStyle="{ height: '100%', padding: 0 }"
    >
      <template #header>
        <div class="preview-header">
          <i class="ri-eye-line"></i>
          <span>{{ $t("dashboardAdmin.previewTitle") }}</span>
        </div>
      </template>
      <div class="preview-container">
        <DashboardComponent
          v-if="showPreview"
          :availableConfigs="[previewConfig]"
        />
      </div>
    </PrimeDialog>

    <!-- Sidebar for block configuration -->
    <div class="sidebar">
      <BlockConfigEditor
        v-if="selectedBlock"
        :block="selectedBlock"
        :custom-block-types="customBlockTypes"
        :custom-route-options="customRouteOptions"
        @update:block="updateBlock"
        @close="selectedBlockId = null"
      />
      <div v-else class="sidebar-empty">
        {{ $t("dashboardAdmin.selectBlockToEdit") }}
      </div>
    </div>

    <!-- Block Type Selector Dialog -->
    <BlockTypeSelector
      v-model="showBlockTypeSelector"
      :custom-block-types="customBlockTypes"
      @select="onBlockTypeSelected"
    />

    <!-- Confirmation Dialog -->
    <PrimeConfirmDialog group="dashboardEditor" />
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import { useToast } from "primevue/usetoast";
import { useI18n } from "vue-i18n";
import Button from "primevue/button";
import InputNumber from "primevue/inputnumber";
import Dialog from "primevue/dialog";
import BlockConfigEditor from "@/components/dashboard/editor/BlockConfigEditor.vue";
import DashboardComponent from "@/components/dashboard/DashboardComponent.vue";
import GridEditor from "@/components/dashboard/editor/grid/GridEditor.vue";
import BasketPanel from "@/components/dashboard/editor/grid/BasketPanel.vue";
import MinimapPanel from "@/components/dashboard/editor/grid/MinimapPanel.vue";
import BlockTypeSelector from "@/components/dashboard/editor/grid/BlockTypeSelector.vue";
import { useDragDrop } from "@/composables/useDragDrop.js";
import { useResize } from "@/composables/useResize.js";
import { useGridValidation } from "@/composables/useGridValidation.js";
import ConfirmDialog from "primevue/confirmdialog";
import { useConfirm } from "primevue/useconfirm";

export default {
  name: "DashboardEditor",
  components: {
    PrimeButton: Button,
    PrimeInputNumber: InputNumber,
    PrimeDialog: Dialog,
    PrimeConfirmDialog: ConfirmDialog,
    BlockConfigEditor,
    DashboardComponent,
    GridEditor,
    BasketPanel,
    MinimapPanel,
    BlockTypeSelector,
  },
  props: {
    config: {
      type: Object,
      required: true,
    },
    customBlockTypes: {
      type: Array,
      default: () => [],
    },
    customRouteOptions: {
      type: Array,
      default: null,
    },
  },
  emits: ["update:config"],
  setup(props, { emit }) {
    const toast = useToast();
    const confirm = useConfirm();
    const { t } = useI18n();

    // State
    const gridSettings = ref({ ...props.config.configJson.gridSettings });
    const blocks = ref([...props.config.configJson.blocConfig]);
    const basketBlocks = ref([]);
    const selectedBlockId = ref(null);
    const showMinimap = ref(false);
    const showBasket = ref(false);
    const showPreview = ref(false);
    const showBlockTypeSelector = ref(false);
    const scrollContainer = ref(null);

    // Composables
    const {
      draggedBlock,
      draggedFromBasket,
      dragPreview,
      onDragStart,
      onBasketDragStart,
      onDragOver,
      onDragLeave,
      onDrop: handleDrop,
      onDragEnd,
    } = useDragDrop(gridSettings, blocks, basketBlocks);

    const { resizingBlock, startResize, onResizeMove, onResizeEnd } = useResize(
      gridSettings,
      blocks,
    );

    const { hasOverlap, validateAndAdjustGrid, updateGridSettings } =
      useGridValidation(gridSettings, blocks, toast, t);

    // Computed
    const selectedBlock = computed(() => {
      return (
        blocks.value.find((b) => b.id === selectedBlockId.value) ||
        basketBlocks.value.find((b) => b.id === selectedBlockId.value)
      );
    });

    const previewConfig = computed(() => {
      return {
        key: props.config.key || "preview",
        label: props.config.label || "Preview",
        configJson: {
          gridSettings: gridSettings.value,
          blocConfig: blocks.value,
        },
      };
    });

    const overlappingBlockIds = computed(() => {
      return blocks.value.filter((block) => hasOverlap(block)).map((b) => b.id);
    });

    // Watch config changes
    watch(
      () => props.config,
      (newConfig) => {
        gridSettings.value = { ...newConfig.configJson.gridSettings };
        blocks.value = [...newConfig.configJson.blocConfig];
        validateAndAdjustGrid(emitUpdate);
      },
      { deep: true },
    );

    // Methods
    const emitUpdate = () => {
      const updatedConfig = {
        ...props.config,
        configJson: {
          gridSettings: gridSettings.value,
          blocConfig: blocks.value,
        },
      };
      emit("update:config", updatedConfig);
    };

    const handleUpdateGridSettings = () => {
      updateGridSettings(emitUpdate);
    };

    const selectBlock = (block) => {
      selectedBlockId.value = block.id;
    };

    const editBlock = (block) => {
      selectedBlockId.value = block.id;
    };

    const addNewBlock = () => {
      showBlockTypeSelector.value = true;
    };

    const getDefaultConfig = (type) => {
      // Check custom block types first
      const customType = props.customBlockTypes.find((ct) => ct.value === type);
      if (customType) {
        return customType.defaultConfig || {};
      }

      const defaults = {
        markdown: {
          id: "",
          markdown: `# ${t("dashboardAdmin.newBlockTitle")}\n${t("dashboardAdmin.newBlockContent")}`,
        },
        chart: {
          type: "pie",
          data: { labels: [], datasets: [] },
          options: { responsive: true, maintainAspectRatio: false },
        },
        favorites: { filter: "file" },
        pdf: { id: "" },
        image: { id: "" },
        bookmarks: { bookmarks: [] },
        externalApps: { links: [], useFavicon: false },
      };
      return defaults[type] || {};
    };

    const getDefaultRoute = (type) => {
      // Check custom block types first
      const customType = props.customBlockTypes.find((ct) => ct.value === type);
      if (customType?.defaultRoute) {
        return customType.defaultRoute;
      }

      return undefined;
    };

    const onBlockTypeSelected = (type) => {
      const title = t("dashboardAdmin.blockTypes." + type);
      const newBlock = {
        id: `block-${Date.now()}`,
        type,
        title,
        config: getDefaultConfig(type),
      };
      const route = getDefaultRoute(type);
      if (route) {
        newBlock.route = route;
      }
      basketBlocks.value.push(newBlock);
      selectedBlockId.value = newBlock.id;
      showBasket.value = true;
    };

    const deleteBlockById = (block) => {
      confirm.require({
        group: "dashboardEditor",
        message: t("dashboardAdmin.confirmDeleteBlock", {
          blockName: block.title || block.type,
        }),
        header: t("dashboardAdmin.deleteConfirmation"),
        icon: "ri-alert-line",
        acceptLabel: t("dashboardAdmin.delete"),
        rejectLabel: t("dashboardAdmin.cancel"),
        accept: () => {
          const index = blocks.value.findIndex((b) => b.id === block.id);
          if (index !== -1) {
            blocks.value.splice(index, 1);
            selectedBlockId.value = null;
            emitUpdate();
            toast.add({
              severity: "success",
              summary: t("success"),
              detail: t("dashboardAdmin.blockDeleted"),
              life: 2000,
            });
          }
        },
      });
    };

    const moveToBasket = (block) => {
      const index = blocks.value.findIndex((b) => b.id === block.id);
      if (index !== -1) {
        blocks.value.splice(index, 1);
        const basketBlock = { ...block };
        delete basketBlock.gridPosition;
        basketBlocks.value.push(basketBlock);
        emitUpdate();
        showBasket.value = true;
        toast.add({
          severity: "info",
          summary: t("dashboardAdmin.movedToBasket"),
          detail: t("dashboardAdmin.movedToBasketDetail"),
          life: 2000,
        });
      }
    };

    const deleteBasketBlock = (block) => {
      confirm.require({
        group: "dashboardEditor",
        message: t("dashboardAdmin.confirmDeleteBlock", {
          blockName: block.title || block.type,
        }),
        header: t("dashboardAdmin.deleteConfirmation"),
        icon: "ri-alert-line",
        acceptLabel: t("dashboardAdmin.delete"),
        rejectLabel: t("dashboardAdmin.cancel"),
        accept: () => {
          const index = basketBlocks.value.findIndex((b) => b.id === block.id);
          if (index !== -1) {
            basketBlocks.value.splice(index, 1);
            if (selectedBlockId.value === block.id) {
              selectedBlockId.value = null;
            }
            toast.add({
              severity: "success",
              summary: t("success"),
              detail: t("dashboardAdmin.blockDeleted"),
              life: 2000,
            });
          }
        },
      });
    };

    const duplicateBlock = (block) => {
      const duplicated = JSON.parse(JSON.stringify(block));
      duplicated.id = `block-${Date.now()}`;

      duplicated.gridPosition.colStart = Math.min(
        duplicated.gridPosition.colStart + 1,
        gridSettings.value.columns,
      );
      duplicated.gridPosition.colEnd = Math.min(
        duplicated.gridPosition.colEnd + 1,
        gridSettings.value.columns + 1,
      );

      if (duplicated.title) {
        duplicated.title = `${duplicated.title} (copie)`;
      }

      blocks.value.push(duplicated);
      emitUpdate();
      selectedBlockId.value = duplicated.id;

      toast.add({
        severity: "success",
        summary: t("success"),
        detail: t("dashboardAdmin.blockDuplicated"),
        life: 2000,
      });
    };

    const updateBlock = (updatedBlock) => {
      const gridIndex = blocks.value.findIndex((b) => b.id === updatedBlock.id);
      if (gridIndex !== -1) {
        blocks.value[gridIndex] = updatedBlock;
        emitUpdate();
        return;
      }

      const basketIndex = basketBlocks.value.findIndex(
        (b) => b.id === updatedBlock.id,
      );
      if (basketIndex !== -1) {
        const basketBlock = { ...updatedBlock };
        delete basketBlock.gridPosition;
        basketBlocks.value[basketIndex] = basketBlock;
      }
    };

    const onDrop = (event) => {
      handleDrop(event, emitUpdate);
    };

    const onMinimapClick = (event) => {
      // Get the minimap-grid element for accurate calculations
      const minimapGrid =
        event.target.closest(".minimap-grid") ||
        event.currentTarget.querySelector(".minimap-grid");
      if (!minimapGrid) {
        return;
      }

      const rect = minimapGrid.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      const xPercent = x / rect.width;
      const yPercent = y / rect.height;

      const clickedCol = Math.floor(xPercent * gridSettings.value.columns) + 1;
      const clickedRow = Math.floor(yPercent * gridSettings.value.rows) + 1;

      // Find the clicked block
      const targetBlock = blocks.value.find((block) => {
        const inColRange =
          clickedCol >= block.gridPosition.colStart &&
          clickedCol < block.gridPosition.colEnd;
        const inRowRange =
          clickedRow >= block.gridPosition.rowStart &&
          clickedRow < block.gridPosition.rowEnd;
        return inColRange && inRowRange;
      });

      if (!targetBlock) {
        return;
      }

      // Select the block
      selectedBlockId.value = targetBlock.id;

      // Find the block element in the DOM by matching data or key attributes
      const blockElements = document.querySelectorAll(".block-editor");
      let targetElement = null;

      blockElements.forEach((el) => {
        const style = el.style.gridColumn;
        const expectedStyle = `${targetBlock.gridPosition.colStart} / ${targetBlock.gridPosition.colEnd}`;
        if (style === expectedStyle) {
          // Double check with gridRow to ensure it's the right block
          const rowStyle = el.style.gridRow;
          const expectedRowStyle = `${targetBlock.gridPosition.rowStart} / ${targetBlock.gridPosition.rowEnd}`;
          if (rowStyle === expectedRowStyle) {
            targetElement = el;
          }
        }
      });

      if (!targetElement) {
        return;
      }

      // Scroll to the block
      const container = scrollContainer.value;
      if (container) {
        targetElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      }
    };

    // Lifecycle
    onMounted(() => {
      document.addEventListener("mousemove", onResizeMove);
      document.addEventListener("mouseup", () => onResizeEnd(emitUpdate));
      validateAndAdjustGrid(emitUpdate);
    });

    onBeforeUnmount(() => {
      document.removeEventListener("mousemove", onResizeMove);
      document.removeEventListener("mouseup", () => onResizeEnd(emitUpdate));
    });

    return {
      // State
      gridSettings,
      blocks,
      basketBlocks,
      selectedBlockId,
      showMinimap,
      showBasket,
      showPreview,
      showBlockTypeSelector,
      scrollContainer,
      // Composable returns
      draggedBlock,
      draggedFromBasket,
      dragPreview,
      resizingBlock,
      overlappingBlockIds,
      // Computed
      selectedBlock,
      previewConfig,
      // Methods
      handleUpdateGridSettings,
      selectBlock,
      editBlock,
      addNewBlock,
      onBlockTypeSelected,
      deleteBlockById,
      moveToBasket,
      deleteBasketBlock,
      duplicateBlock,
      updateBlock,
      onDragStart,
      onBasketDragStart,
      onDragOver,
      onDragLeave,
      onDrop,
      onDragEnd,
      startResize,
      onMinimapClick,
    };
  },
};
</script>

<style scoped>
.dashboard-editor {
  display: flex;
  height: 100%;
  background: var(--p-surface-ground);
}

.editor-main {
  flex: 1;
  padding: 1rem;
  overflow: auto;
}

.toolbar-row {
  margin-bottom: 0.75rem;
  display: flex;
  align-items: stretch;
  gap: 0.75rem;
}

.toolbar-group {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.toolbar-actions {
  display: flex;
  gap: 0.5rem;
}

.toolbar-spacer {
  flex: 1;
}

.block-count {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  color: var(--p-text-muted-color);
}

.preview-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.sidebar {
  width: 30rem;
  overflow-y: auto;
  flex-shrink: 0;
  background: var(--p-content-background);
  border-left: 1px solid var(--p-content-border-color);
}

.sidebar-empty {
  padding: 1rem;
  text-align: center;
  color: var(--p-text-muted-color);
}

/* Preview Dialog */
.preview-container {
  height: 100%;
  width: 100%;
  padding: 1rem;
  background: var(--p-surface-ground);
  overflow: auto;
}

/* Grid size inputs */
.grid-size-input {
  width: 5rem;
}

.grid-size-input :deep(.p-inputnumber-input) {
  width: 100%;
}
</style>
